import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  LabelList,
} from 'recharts'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table'
import { toast } from 'sonner'
import { PlusIcon, Trash2Icon, CheckCircle2Icon } from 'lucide-react'

import {
  apiClient, listFlexibleItems,
  type FlexibleItemRouting, type NarrativeGuard,
} from '@/api/client'
import {
  EMPTY_NARRATIVE, narrativeFieldsFrom, type NarrativeFields,
} from '@/api/narrative'
import { AiNarrativePanel } from '@/components/qoe/AiNarrativePanel'
import { formatApiError } from '@/utils/httpErrors'
import * as fm from '@/lib/financialMath'
import { routeFlexibleItems, type FlexibleLineItem } from '@/lib/flexibleLineItems'
import {
  abbreviateCurrency, axisWidthFor, isImplausibleMagnitude, niceTicks, placeBarLabel,
} from '@/lib/chartScale'
import {
  CURRENCY_OPTIONS, DEFAULT_CURRENCY, currencySymbol, formatCurrency,
  formatNumericInput, localeDefaultCurrency, numericInputValue, parseNumericInput,
} from '@/lib/currency'
import { useAuth } from '@/contexts/AuthContext'
import type { Deal, EbitdaAdjustment, NormalizationSession } from '@/types/deal'
import { QoEFinancialStatementCard } from '@/components/qoe/QoEFinancialStatementCard'
import type { QoEFinancialStatement } from '@/components/qoe/QoEFinancialStatementCard'
import { ExtractionPanel, ProvenanceBadge } from '@/components/qoe/ExtractionPanel'
import type { ProvenanceMap } from '@/components/qoe/ExtractionPanel'
import { MultiYearTrendCard } from '@/components/qoe/MultiYearTrendCard'
import { BalanceSheetInputs } from '@/components/qoe/BalanceSheetInputs'
import { EquityBridgeInputs } from '@/components/qoe/EquityBridgeInputs'
import {
  AddLineItemInline, FlexibleLineItemsPanel,
} from '@/components/qoe/FlexibleLineItems'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

// ── Constants ────────────────────────────────────────────────────────────────

const CY = new Date().getFullYear()
// The window a brand-new deal opens with. EXTRACT-PERIOD-1: this used to be the
// *only* window — a fixed three tabs — so a statement covering FY2023 or a
// forecast column for FY2027 was read by the backend, written to the deal, and
// then silently discarded on load because no tab existed to hold it. It is now
// only what a deal with no periods at all opens on — see `deriveYears`.
const DEFAULT_YEARS = [CY - 2, CY - 1, CY] as const

/** The years this deal actually has, oldest first — or the starting window if
 *  it has none yet.
 *
 *  EXTRACT-PERIOD-2. This used to union the deal's periods with a fixed
 *  [CY-2..CY] window, so a two-year engagement still showed three tabs and a
 *  four-year one showed five. Empty tabs are not neutral on a CPA workspace:
 *  they assert a history the documents never contained, and the Balance Sheet
 *  (which derives its columns honestly) then disagreed with the P&L about how
 *  many periods the deal has. The periods present in the uploaded or entered
 *  sources are the whole answer.
 *
 *  Nothing the server returned is ever dropped: a year that disappears from the
 *  grid also disappears from the next auto-save payload, so dropping one on
 *  load *deletes* it.
 */
function deriveYears(present: readonly number[]): number[] {
  const all = new Set<number>()
  for (const y of present) {
    if (Number.isFinite(y)) all.add(Math.trunc(y))
  }
  if (all.size === 0) return [...DEFAULT_YEARS]
  return [...all].sort((a, b) => a - b)
}

const CATEGORIES = [
  'Owner Comp: Salary',
  'Owner Comp: Bonus',
  'Owner Comp: Retirement',
  'Owner Benefits: Health Insurance',
  'Owner Benefits: Life Insurance',
  'Owner Benefits: Personal Vehicle',
  'Owner Benefits: Personal Travel',
  'Owner Benefits: Club/Memberships',
  'One-Time: Legal Settlement',
  'One-Time: Equipment',
  'One-Time: Severance',
  'One-Time: Pandemic/Disaster',
  'Non-Cash: Depreciation',
  'Non-Cash: Amortization',
  'Related Party: Owner Rent',
  'Related Party: Management Fee',
  'Related Party: Loan Interest',
  'Run-Rate: New Contract',
  'Run-Rate: Cost Reduction',
  'Run-Rate: Headcount Change',
] as const

// UI-POLISH-1 spec: single gold hue; final (Adj. EBITDA) bar full opacity, others ~55%.
const BAR_COLOR = 'var(--color-accent)'

// ── Types ────────────────────────────────────────────────────────────────────

interface PAndLRaw {
  revenue: number | ''
  cogs: number | ''
  operating_expenses: number | ''
  depreciation: number | ''
  amortization: number | ''
  interest: number | ''
  tax: number | ''
}

type NormalizationResponse = NormalizationSession & {
  // ENG-AI-1 — the AI draft and the PROSE gate around it. Two gates live on this
  // response and they are independent: `narrative_status` decides whether the
  // draft renders into the report, `status` below decides whether the report may
  // be generated at all. Approving one never approves the other.
  ai_narrative?: string | null
  narrative_status?: string | null
  narrative_guard?: NarrativeGuard | null
  narrative_generated_by?: string | null
  narrative_approved_by?: string | null
  narrative_approved_at?: string | null
  // The over-cap sentinel from POST .../generate-narrative (VALIDATION-1C):
  // a 200 carrying status "unavailable" rather than an error, because an
  // exhausted spend cap is our budget problem, not the CPA's fault.
  reason?: string | null
  message?: string | null
  // REPORTGATE-WIRE-1: the CPA sign-off on the normalized statement. `status`
  // is "draft" | "approved"; "approved" is what enables Generate Report.
  status?: string | null
  approved_by?: string | null
  approved_at?: string | null
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const emptyPL = (): PAndLRaw => ({
  revenue: '', cogs: '', operating_expenses: '', depreciation: '', amortization: '',
  interest: '', tax: '',
})

const numOrNull = (v: number | ''): number | null => (v === '' ? null : v)

// QOE-CALC-1: every derived P&L figure comes from `@/lib/financialMath` —
// the TS mirror of core/financial_math.py. No local arithmetic on financial
// fields belongs in this file. The old local helpers computed
// `net_income = gross_profit - opex` (missing D, A, I, T) and then derived
// EBITDA from that broken net income, overstating BOTH by (D + A + I + T).
const calcGP = (r: PAndLRaw): number | '' => fm.grossProfit(r.revenue, r.cogs)

const calcNI = (r: PAndLRaw): number | '' =>
  fm.netIncome(r.revenue, r.cogs, r.operating_expenses,
    r.depreciation, r.amortization, r.interest, r.tax)

const calcEB = (r: PAndLRaw): number | '' =>
  fm.ebitda(calcGP(r), r.operating_expenses)

// CURRENCY-1: chart axis/label shorthand. Compact by design — a bar chart with
// "$2,150,000.00" on every bar is unreadable — but it carries the deal's own
// symbol instead of a hardcoded "$".
//
// WS-VIEW-3: this used to know only two tiers, M and K, so a billion-scale
// figure was still divided by 1e6 and printed "$1000.0M" — too wide for the axis
// gutter, and the clipped remains read as a column of identical "00000.0M"
// ticks. `abbreviateCurrency` carries K/M/B/T and bounds the string length, so
// the gutter can be sized from the ticks it is actually going to draw.
const makeAxisFormatter = (currency: string) => {
  const sym = currencySymbol(currency)
  return (val: number): string => abbreviateCurrency(val, sym)
}

/** Props recharts hands a `LabelList` custom renderer. `viewBox` is spread onto
 *  the entry root, and `parentViewBox` is the plot area. */
interface BarLabelRenderProps {
  x?: number | string
  y?: number | string
  width?: number | string
  height?: number | string
  // Widest recharts allows (its RenderableText includes null and arrays), so the
  // renderer stays assignable to LabelList's `content`.
  value?: unknown
  // `object`, not a shape: recharts' ViewBox is a union of the cartesian and
  // polar boxes, and the renderer has to stay assignable to `content`. The
  // numbers are read back through `numProp`, which checks each one.
  parentViewBox?: object
}

const asNumber = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Read a numeric field off a loosely-typed viewBox, or undefined if absent. */
const numProp = (obj: object | undefined, key: string): number | undefined => {
  const v = (obj as Record<string, unknown> | undefined)?.[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/**
 * WS-VIEW-3 bar value label.
 *
 * `position="top" offset={4}` was fine until the bars had very different
 * magnitudes: EBITDA against a revenue-scale axis is a few pixels tall, so its
 * value landed on the "EBITDA" category tick text below it. `placeBarLabel`
 * keeps the label inside the plot area and out of the axis band, and hides it
 * outright when it is wider than its category slot — the exact figures are in
 * the Summary panel and the tooltip, so a legible chart beats a complete but
 * unreadable one.
 */
const makeBridgeLabelRenderer = (
  fmt: (val: number) => string,
  barCount: number,
) => (props: BarLabelRenderProps) => {
  const value = typeof props.value === 'number' ? props.value : Number(props.value)
  if (!Number.isFinite(value)) return <></>

  const plotY = numProp(props.parentViewBox, 'y')
  const plotHeight = numProp(props.parentViewBox, 'height')
  const plot = plotY != null && plotHeight != null ? { y: plotY, height: plotHeight } : null
  // The slot, not the bar: a narrow bar may overhang its own slot harmlessly.
  const plotWidth = numProp(props.parentViewBox, 'width')
  const slotWidth =
    plotWidth != null && barCount > 0 ? plotWidth / barCount - 4 : undefined

  const text = fmt(value)
  const { x, y, visible } = placeBarLabel(
    {
      x: asNumber(props.x),
      y: asNumber(props.y),
      width: asNumber(props.width),
      height: asNumber(props.height),
    },
    { plot, text, fontSize: 10, slotWidth },
  )
  if (!visible) return <></>

  // One fill for both placements: every bar is the gold accent, so the navy
  // brand text is legible whether the label sits above the bar or inside it.
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      className="fill-brand-text"
      style={{ fontSize: 10, fontWeight: 600 }}
    >
      {text}
    </text>
  )
}

let _adjCounter = 0
const newAdjId = (): string => `adj-new-${++_adjCounter}-${Date.now()}`

// ── Component ────────────────────────────────────────────────────────────────

export function Normalization() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  // DEAL-PERSIST-1: the URL query string is the single source of truth for which
  // deal is open. It used to be component state seeded from the query string
  // once, so any remount — a route re-render, a return to the workspace — reset
  // the selector to "— Select a deal —" mid-entry, and autosave was then left
  // with no deal to write the analyst's figures to.
  const dealId = new URLSearchParams(location.search).get('deal_id')
  const selectDeal = useCallback((next: string | null) => {
    const params = new URLSearchParams(location.search)
    if (next) params.set('deal_id', next)
    else params.delete('deal_id')
    // replace: picking a deal is not a navigation step the back button should
    // have to walk through.
    navigate({ search: params.toString() }, { replace: true })
  }, [location.search, navigate])

  // ── State ──
  const [deals, setDeals] = useState<Deal[]>([])
  const [deal, setDeal] = useState<Deal | null>(null)
  const [normalization, setNormalization] = useState<NormalizationResponse | null>(null)
  const [loadingNorm, setLoadingNorm] = useState(false)
  const [pl, setPl] = useState<Record<number, PAndLRaw>>(
    () => Object.fromEntries(DEFAULT_YEARS.map(y => [y, emptyPL()]))
  )
  // EXTRACT-PERIOD-1: which fiscal years this deal has, not which three the
  // calendar suggests. Every year here gets a tab, a column and a slot in the
  // save payload.
  const [years, setYears] = useState<number[]>(() => deriveYears([]))
  const [adjustments, setAdjustments] = useState<EbitdaAdjustment[]>([])
  const [saveStatus, setSaveStatus] = useState<'idle' | 'dirty' | 'saving' | 'saved' | 'error'>('idle')
  // REPORTGATE-WIRE-1: in flight while the CPA's sign-off POST is outstanding.
  const [approving, setApproving] = useState(false)
  // ENG-AI-UI-RENDER-1: the narrative is its own state slice, not a few keys on
  // `normalization`. Merging the generate response into the session object is
  // what previously overwrote the statement gate (`status`) with the
  // generation outcome ("ok") and left `ai_narrative` unset, so a saved draft
  // stayed invisible until a reload. The panel owns its own busy flag.
  const [narrative, setNarrative] = useState<NarrativeFields>(EMPTY_NARRATIVE)
  const [qoeFs, setQoeFs] = useState<QoEFinancialStatement | null>(null)
  // DOC-INTAKE-1 Job 2 — provenance for every machine-filled cell, keyed
  // "<year>.<field>". Kept separate from `pl` so an auto-save never rewrites it.
  const [provenance, setProvenance] = useState<ProvenanceMap>({})
  const [lastRun, setLastRun] = useState<any>(null)
  const [extractionNonce, setExtractionNonce] = useState(0)
  // EXTRACT-REFRESH-1: the provenance GET below had no loading flag at all —
  // `loadingNorm` and `loadingFlexible` (below) already tracked theirs. All
  // three feed `extractionRefreshing`, which is what tells ExtractionPanel the
  // post-run reload is still in flight, so it stays a proxy for "this fetch is
  // outstanding" even outside an extraction run (e.g. a deal switch); that is
  // harmless — the reload the panel is honestly reporting on is real either way.
  const [provenanceLoading, setProvenanceLoading] = useState(false)
  // WORKSPACE-INPUT-1(c): the FY switcher is a controlled segmented control, not
  // the shared <Tabs>. The shared component's active state is a light fill on a
  // muted rail, which read as "all three years look the same" in live testing —
  // and which year you are editing is the single most consequential piece of
  // state on this page. Owned locally so the contrast is guaranteed.
  const [activeYear, setActiveYear] = useState<number>(CY)
  // CURRENCY-1: the deal's reporting currency. Before a deal is picked we show
  // the browser-locale guess, which is also what a newly created deal gets.
  const [currency, setCurrency] = useState<string>(() => localeDefaultCurrency())
  const [savingCurrency, setSavingCurrency] = useState(false)
  // In-progress text for the numeric fields, keyed "<year>.<field>" / adjustment
  // id. Without it the controlled re-render eats a trailing "." or "0" the
  // moment it is typed, and "1200.05" is impossible to enter.
  const [plDraft, setPlDraft] = useState<Record<string, string>>({})
  const [adjDraft, setAdjDraft] = useState<Record<string, string>>({})
  // FIELDS-2: the deal's custom line items. Their own CRUD endpoint, deliberately
  // outside the auto-saved normalization payload — an item is created, classified
  // or deleted by an explicit act, and must not be swept along by a debounced
  // save of the P&L grid. `flexibleNonce` is bumped after any mutation to reload.
  const [flexibleItems, setFlexibleItems] = useState<FlexibleLineItem[]>([])
  const [flexibleRouting, setFlexibleRouting] = useState<FlexibleItemRouting | null>(null)
  const [flexibleMeanings, setFlexibleMeanings] = useState<Record<string, string> | null>(null)
  const [loadingFlexible, setLoadingFlexible] = useState(false)
  const [flexibleNonce, setFlexibleNonce] = useState(0)

  // ── Refs (avoid stale closures in timer callbacks) ──
  const plRef = useRef(pl)
  const adjRef = useRef(adjustments)
  // Same reason as `plRef`: a debounced save fires from a closure minted before
  // an extraction widened the grid. Reading the year list from a ref means the
  // PUT carries the years on screen now, not the ones on screen when the user
  // last typed.
  const yearsRef = useRef(years)
  plRef.current = pl
  adjRef.current = adjustments
  yearsRef.current = years
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // DEAL-PERSIST-1: every edit bumps a revision. A save records the revision it
  // carried, and only claims "All changes saved" if nothing was typed while it
  // was in flight — otherwise a slow PUT reports success over keystrokes it
  // never sent, which is the one thing a save indicator must never do.
  const revisionRef = useRef(0)

  // CURRENCY-1: one formatter for the whole page, bound to the deal's currency.
  // Every figure — grid, computed totals, adjustments, KPIs — reads the same way.
  const fmtCurrency = useCallback(
    (val: number | null | undefined) => formatCurrency(val, currency),
    [currency],
  )
  const fmtAxis = useMemo(() => makeAxisFormatter(currency), [currency])
  const symbol = useMemo(() => currencySymbol(currency), [currency])

  // buildPayload reads from refs so any timer closure gets fresh data
  const buildPayload = () => ({
    financial_years: yearsRef.current.map(y => {
      const raw = plRef.current[y] ?? emptyPL()
      return {
        year: y,
        revenue: numOrNull(raw.revenue),
        cogs: numOrNull(raw.cogs),
        gross_profit: numOrNull(calcGP(raw)),
        operating_expenses: numOrNull(raw.operating_expenses),
        net_income: numOrNull(calcNI(raw)),
        ebitda: numOrNull(calcEB(raw)),
        depreciation: numOrNull(raw.depreciation),
        amortization: numOrNull(raw.amortization),
        interest: numOrNull(raw.interest),
        tax: numOrNull(raw.tax),
      }
    }),
    adjustments: adjRef.current,
  })

  // ── Auto-save ──
  const performSave = useCallback(async (revision: number) => {
    if (!dealId) return
    setSaveStatus('saving')
    try {
      await apiClient.put(`/qoe/deals/${dealId}/normalization`, buildPayload())
      // Newer keystrokes landed while this was in flight; a later save is
      // already queued for them, so leave the indicator alone.
      if (revisionRef.current !== revision) return
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId])

  const scheduleAutoSave = useCallback(() => {
    if (!dealId) return
    const revision = ++revisionRef.current
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    setSaveStatus('dirty')
    autoSaveTimer.current = setTimeout(() => { void performSave(revision) }, 2000)
  }, [dealId, performSave])

  const retrySave = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    void performSave(revisionRef.current)
  }, [performSave])

  // ── Load deals list ──
  useEffect(() => {
    apiClient.get<{ deals: Deal[]; count: number }>('/qoe/deals')
      .then(r => setDeals(r.data.deals))
      .catch(() => toast.error('Failed to load deals'))
  }, [])

  // ── Load normalization session when deal changes ──
  useEffect(() => {
    setPlDraft({})
    setAdjDraft({})
    // A debounced save still pending here was queued against the PREVIOUS deal,
    // but reads the refs — which are about to hold this deal's figures. Drop it.
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current)
      autoSaveTimer.current = null
    }
    revisionRef.current += 1
    setSaveStatus('idle')
    if (!dealId) {
      setNormalization(null)
      setNarrative(EMPTY_NARRATIVE)
      setDeal(null)
      setYears(deriveYears([]))
      setPl(Object.fromEntries(DEFAULT_YEARS.map(y => [y, emptyPL()])))
      setActiveYear(CY)
      setAdjustments([])
      setCurrency(localeDefaultCurrency())
      return
    }
    setLoadingNorm(true)
    const selected = deals.find(d => d.deal_id === dealId) ?? null
    setDeal(selected)
    // CURRENCY-1: deals created before the column existed come back without one.
    if (selected) setCurrency(selected.currency || DEFAULT_CURRENCY)

    apiClient.get<NormalizationResponse>(`/qoe/deals/${dealId}/normalization`)
      .then(r => {
        const norm = r.data
        setNormalization(norm)
        // The saved draft, if the deal already has one. Lifted out of the
        // session so the panel has a single source whether the draft arrived
        // from this GET or from a generate that just returned.
        setNarrative(narrativeFieldsFrom(norm))
        // EXTRACT-PERIOD-1: build the grid from the years this deal actually
        // has. Iterating a fixed [CY-2..CY] here is what made an extraction
        // report "12 fields filled" while the CPA saw three empty tabs — the
        // figures were saved against FY2023, and FY2023 had no tab to look in.
        const dealYears = deriveYears(
          (norm.financial_years ?? []).map(f => f.year)
        )
        setYears(dealYears)
        // Keep the open tab pointing at a year that still exists. Falls back to
        // the newest, which is the year a CPA is normally working in.
        setActiveYear(prev =>
          dealYears.includes(prev) ? prev : dealYears[dealYears.length - 1]
        )
        const loaded = Object.fromEntries(dealYears.map(y => {
          const fy = norm.financial_years.find(f => f.year === y)
          return [y, {
            revenue: fy?.revenue ?? ('' as const),
            cogs: fy?.cogs ?? ('' as const),
            operating_expenses: fy?.operating_expenses ?? ('' as const),
            depreciation: fy?.depreciation ?? ('' as const),
            amortization: fy?.amortization ?? ('' as const),
            interest: fy?.interest ?? ('' as const),
            tax: fy?.tax ?? ('' as const),
          }]
        }))
        setPl(loaded)
        setAdjustments(norm.adjustments ?? [])
      })
      .catch(() => toast.error('Failed to load normalization data'))
      .finally(() => setLoadingNorm(false))
    // extractionNonce: an extraction run rewrites the grid server-side, so the
    // page must re-read it rather than keep the pre-run values on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, deals.length, extractionNonce])

  // ── Load extraction provenance (DOC-INTAKE-1 Job 2) ──
  useEffect(() => {
    if (!dealId) { setProvenance({}); setLastRun(null); return }
    setProvenanceLoading(true)
    apiClient
      .get<{ provenance: ProvenanceMap; last_run: any }>(`/qoe/deals/${dealId}/extraction`)
      .then(r => {
        setProvenance(r.data.provenance ?? {})
        setLastRun(r.data.last_run ?? null)
      })
      // Absent provenance is the normal case for a deal nobody has extracted.
      .catch(() => { setProvenance({}); setLastRun(null) })
      .finally(() => setProvenanceLoading(false))
  }, [dealId, extractionNonce])

  // ── Load custom line items (FIELDS-2) ──
  // extractionNonce is a dependency because an extraction run parks its unmapped
  // lines as treatment-less items — they must appear in the review queue the
  // moment the run finishes, not on the next full page load.
  useEffect(() => {
    if (!dealId) {
      setFlexibleItems([])
      setFlexibleRouting(null)
      setFlexibleMeanings(null)
      return
    }
    setLoadingFlexible(true)
    listFlexibleItems(dealId)
      .then(r => {
        setFlexibleItems(r.items ?? [])
        setFlexibleRouting(r.routing ?? null)
        setFlexibleMeanings(r.treatments ?? null)
      })
      // A deal with no custom items yet is the ordinary early state. Fall back to
      // an empty list rather than a toast, so the derived figures below simply
      // reflect the reported statements — which is the correct answer.
      .catch(() => {
        setFlexibleItems([])
        setFlexibleRouting(null)
      })
      .finally(() => setLoadingFlexible(false))
  }, [dealId, flexibleNonce, extractionNonce])

  const flexChanged = useCallback(() => setFlexibleNonce(n => n + 1), [])

  // ── Load approved QoE financial statement when deal changes ──
  useEffect(() => {
    if (!dealId) { setQoeFs(null); return }
    apiClient
      .get<QoEFinancialStatement[]>(`/v1/qoe/deals/${dealId}/financial-statements`, { params: { status: 'approved', limit: 1 } })
      .then(r => setQoeFs(r.data[0] ?? null))
      .catch(() => setQoeFs(null))
  }, [dealId])

  // Drop any pending save on unmount
  useEffect(() => () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
  }, [])

  // ── Input handlers ──
  const updatePL = useCallback((year: number, field: keyof PAndLRaw, value: number | '') => {
    setPl(prev => ({ ...prev, [year]: { ...prev[year], [field]: value } }))
    scheduleAutoSave()
  }, [scheduleAutoSave])

  // CURRENCY-1: currency is a property of the deal, not of this screen, so it is
  // persisted immediately rather than riding along with the 2s normalization
  // autosave — every other panel that reads the deal needs the same answer.
  const changeCurrency = useCallback(async (next: string) => {
    const previous = currency
    setCurrency(next)
    if (!dealId) return
    setSavingCurrency(true)
    try {
      await apiClient.patch(`/qoe/deals/${dealId}`, { currency: next })
      setDeals(prev => prev.map(d => (d.deal_id === dealId ? { ...d, currency: next } : d)))
      setDeal(prev => (prev ? { ...prev, currency: next } : prev))
    } catch {
      setCurrency(previous)
      toast.error('The currency could not be saved. Please try again in a moment.')
    } finally {
      setSavingCurrency(false)
    }
  }, [currency, dealId])

  // `boolean` in the union for FIELDS-1 P2's is_pending toggle.
  const updateAdj = useCallback((idx: number, field: keyof EbitdaAdjustment, value: string | number | boolean) => {
    setAdjustments(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a))
    scheduleAutoSave()
  }, [scheduleAutoSave])

  // WS-VIEW-1: a new adjustment lands on the year being worked on, not on the
  // current calendar year. Defaulting to CY while the analyst was on FY2024 put
  // the row in a year whose Summary they were not looking at, so the KPI they
  // expected to move did not move.
  const addAdj = useCallback(() => {
    setAdjustments(prev => [
      ...prev,
      // is_pending explicit rather than left undefined: the checkbox is a
      // three-state trap otherwise (undefined reads as unchecked but never
      // round-trips as false), and the save sends the row verbatim.
      {
        adjustment_id: newAdjId(), category: CATEGORIES[0], description: '',
        amount: 0, year: activeYear, is_pending: false,
      },
    ])
    scheduleAutoSave()
  }, [scheduleAutoSave, activeYear])

  const removeAdj = useCallback((idx: number) => {
    setAdjustments(prev => prev.filter((_, i) => i !== idx))
    scheduleAutoSave()
  }, [scheduleAutoSave])

  // ── Statement sign-off (REPORTGATE-WIRE-1) ──
  //
  // This is the report gate. The analyst prepares and normalizes; the CPA signs
  // the result off, and only then does "Generate Report" enable. The POST is a
  // narrow writer server-side — it touches only status / approved_by /
  // approved_at — so it cannot race the 2s auto-save that rewrites the figures.
  //
  // Flushed first, deliberately: signing off a statement while an unsaved
  // keystroke is still sitting in the debounce would approve figures the
  // database has never seen.
  const approveNormalization = useCallback(async () => {
    if (!dealId) return
    setApproving(true)
    try {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current)
        autoSaveTimer.current = null
      }
      await apiClient.put(`/qoe/deals/${dealId}/normalization`, buildPayload())
      setSaveStatus('saved')
      const res = await apiClient.post<NormalizationResponse>(
        `/qoe/deals/${dealId}/normalization/approve`
      )
      setNormalization(prev => (prev ? { ...prev, ...res.data } : prev))
      toast.success('Normalization approved — the report can now be generated.')
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setApproving(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId])

  // ── AI narrative (ENG-AI-1 / ENG-AI-UI-RENDER-1) ──
  //
  // The three narrative endpoints, the wire-shape mapping and the panel itself
  // now live in `@/api/narrative` + `AiNarrativePanel`. Two things this page
  // must still own:
  //
  //  * the flush — the narrative is written from the SAVED figures, so the
  //    debounced auto-save has to land before generation, exactly as it does
  //    before statement sign-off;
  //  * the state slice — `narrative` is separate from `normalization` so that a
  //    narrative action can never write the statement gate (`status`), and so
  //    a draft still renders when the session GET returned nothing.
  const flushBeforeNarrative = useCallback(async () => {
    if (!dealId) return
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current)
      autoSaveTimer.current = null
    }
    await apiClient.put(`/qoe/deals/${dealId}/normalization`, buildPayload())
    setSaveStatus('saved')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId])

  const patchNarrative = useCallback((patch: Partial<NarrativeFields>) => {
    setNarrative(prev => ({ ...prev, ...patch }))
  }, [])

  // ── Computed values (the fiscal year currently selected above) ──
  //
  // WS-VIEW-1: these were keyed to `CY` while the P&L grid was keyed to
  // `activeYear`. Selecting FY2024, typing FY2024 figures, and reading a
  // Summary panel still titled "FY2026" — showing FY2026's Adj. EBITDA, SDE and
  // bars — is a silent mismatch, and the analyst has no cue that the number
  // next to their cursor belongs to a different year. `activeYear` is now the
  // single source of year state for the grid, the Summary panel and the bridge.
  const activePL = pl[activeYear] ?? emptyPL()

  // Selected-year adjustments, shared by every derived KPI below so the
  // owner-comp split is applied in exactly one place.
  const activeAdjustments = useMemo(
    () => adjustments.filter(a => a.year === activeYear),
    [adjustments, activeYear],
  )

  // FIELDS-2: one derivePeriod call now produces every KPI on this panel, so the
  // custom line items are applied identically to the way the server applies them
  // on read (core/financial_math.derive_period). Deriving each figure separately
  // would have meant routing the treatments three times, and three chances for
  // the screen to disagree with the report.
  //
  // `year` is passed into the raw period because that is how a flexible item
  // attaches to a period — an item for FY2024 must not touch FY2025's EBITDA.
  const activeDerived = useMemo(
    () => fm.derivePeriod({ ...activePL, year: activeYear }, activeAdjustments, flexibleItems),
    [activePL, activeYear, activeAdjustments, flexibleItems],
  )

  const activeEBITDA = activeDerived.ebitda

  // The revenue the derived figures above were actually computed from. Reported
  // revenue plus any `revenue`-treatment line items for this year — without
  // this, a custom revenue item would raise gross profit on the chart while the
  // revenue bar it is measured against stayed flat.
  const activeRevenue = useMemo(() => {
    if (typeof activePL.revenue !== 'number') return activePL.revenue
    return activePL.revenue + routeFlexibleItems(flexibleItems, activeYear).revenueDelta
  }, [activePL.revenue, flexibleItems, activeYear])

  const adjEbitda = useMemo(() => {
    const v = activeDerived.adjusted_ebitda
    return typeof v === 'number' ? v : null
  }, [activeDerived])

  const sde = useMemo(() => {
    const v = activeDerived.sde
    return typeof v === 'number' ? v : null
  }, [activeDerived])

  const sdeMultiple = useMemo(
    () => fm.sdeMultiple(deal?.loi_price ?? '', sde ?? ''),
    [deal, sde],
  )

  // FIELDS-1 P2: the footer total excludes "Still Needed" rows, because every
  // KPI above it does (fm.confirmedAdjustments). A total that counted pending
  // amounts would not reconcile to the Adj. EBITDA card sitting next to it.
  const totalAdj = fm.confirmedAdjustments(adjustments).reduce((s, a) => s + a.amount, 0)
  const pendingCount = adjustments.filter(a => a.is_pending).length

  // Chart series reuses the KPIs computed above — it must never re-derive
  // them, or the chart and the KPI cards can disagree (QOE-CALC-1).
  const waterfallData = useMemo(() => {
    const rev = typeof activeRevenue === 'number' ? Math.max(0, activeRevenue) : 0
    const gp = activeDerived.gross_profit
    return [
      { name: 'Revenue', value: rev },
      { name: 'Gross Profit', value: typeof gp === 'number' ? gp : 0 },
      { name: 'EBITDA', value: typeof activeEBITDA === 'number' ? activeEBITDA : 0 },
      { name: 'Adj. EBITDA', value: adjEbitda ?? 0 },
    ]
  }, [activeRevenue, activeDerived, activeEBITDA, adjEbitda])

  // WS-VIEW-3: round tick steps chosen from the data, and a gutter sized from the
  // widest tick that will actually be drawn. `null` when there is nothing to
  // scale (an untouched deal) — recharts' own defaults are better than a
  // fabricated 0–1 range. The domain also spans below zero when it has to, so a
  // loss-making EBITDA is drawn rather than clipped at the baseline.
  const bridgeScale = useMemo(
    () => niceTicks(waterfallData.map(d => d.value)),
    [waterfallData],
  )
  const bridgeAxisWidth = useMemo(
    () => (bridgeScale ? axisWidthFor(bridgeScale.ticks, fmtAxis) : 56),
    [bridgeScale, fmtAxis],
  )
  const bridgeLabel = useMemo(
    () => makeBridgeLabelRenderer(fmtAxis, waterfallData.length),
    [fmtAxis, waterfallData.length],
  )
  // A soft notice only. The figure is still charted and still saved — this flags
  // a likely stray digit, it does not decide the number is wrong.
  const bridgeImplausible = useMemo(
    () => waterfallData.some(d => isImplausibleMagnitude(d.value)),
    [waterfallData],
  )

  // ── TanStack Table columns ──
  const columns: ColumnDef<EbitdaAdjustment>[] = useMemo(() => [
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => (
        <select
          value={row.original.category}
          onChange={e => updateAdj(row.index, 'category', e.target.value)}
          className="h-7 w-44 rounded border border-input bg-transparent px-1 text-xs outline-none focus:ring-1 focus:ring-ring"
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <Input
          value={row.original.description}
          onChange={e => updateAdj(row.index, 'description', e.target.value)}
          className="h-7 min-w-32 text-xs"
          placeholder="Add note"
        />
      ),
    },
    {
      id: 'amount',
      header: `Amount (${symbol})`,
      // CURRENCY-1: typed with thousands separators, stored as a plain number.
      cell: ({ row }) => {
        const key = row.original.adjustment_id
        return (
          <Input
            type="text"
            inputMode="decimal"
            aria-label="Adjustment amount"
            value={adjDraft[key] ?? numericInputValue(row.original.amount)}
            onChange={e => {
              const shown = formatNumericInput(e.target.value)
              setAdjDraft(prev => ({ ...prev, [key]: shown }))
              const parsed = parseNumericInput(shown)
              updateAdj(row.index, 'amount', parsed === '' ? 0 : parsed)
            }}
            onBlur={() => setAdjDraft(prev => {
              const { [key]: _drop, ...rest } = prev
              return rest
            })}
            className="h-7 w-28 text-xs text-right tabular-nums"
          />
        )
      },
    },
    {
      id: 'year',
      header: 'Year',
      cell: ({ row }) => (
        <select
          value={row.original.year}
          onChange={e => updateAdj(row.index, 'year', parseInt(e.target.value))}
          className="h-7 rounded border border-input bg-transparent px-1 text-xs outline-none focus:ring-1 focus:ring-ring"
        >
          {years.map(y => <option key={y} value={y}>FY{y}</option>)}
        </select>
      ),
    },
    {
      // FIELDS-1 P2: "Still Needed". The engine has always excluded is_pending
      // from every figure (core/financial_math.py, mirrored in
      // lib/financialMath.ts), but nothing in the React workspace could set it —
      // so an add-back the analyst could not yet support had to be either left
      // out of the schedule entirely or counted as if it were supported.
      id: 'pending',
      header: 'Still Needed',
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            aria-label={`Mark "${row.original.description || row.original.category}" as still needed`}
            title="Proposed but not yet supported — excluded from Adj. EBITDA and SDE"
            checked={!!row.original.is_pending}
            onChange={e => updateAdj(row.index, 'is_pending', e.target.checked)}
            className="h-3.5 w-3.5 rounded border-input accent-brand-primary"
          />
        </div>
      ),
    },
    {
      id: 'delete',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => removeAdj(row.index)}
          className="text-destructive hover:text-destructive"
        >
          <Trash2Icon className="h-3.5 w-3.5" />
        </Button>
      ),
    },
    // `years` drives the per-adjustment FY picker, so an extraction that adds a
    // year has to re-render the column or the new year is unselectable.
  ], [updateAdj, removeAdj, symbol, adjDraft, years])

  const table = useReactTable({
    data: adjustments,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const canApprove = user?.role === 'cpa' || user?.role === 'super_admin'

  // ── P&L field renderers (WORKSPACE-INPUT-1(b)) ──
  // Two renderers, deliberately. The old single renderer drew computed totals
  // as read-only <Input>s, so Gross Profit / Net Income / EBITDA looked exactly
  // like something you were supposed to type into. An editable cell now reads
  // as an input (solid field, heavier border, placeholder); a computed cell
  // reads as an output (plain text on a tinted row, no box, no caret).

  const renderPLInput = (year: number, field: keyof PAndLRaw, label: string) => {
    const val = pl[year]?.[field] ?? ''
    const cell = provenance[`${year}.${field}`]
    const edited = !!cell && typeof val === 'number' && Math.abs(val - cell.value) > 0.005
    const draft = !!cell && !edited && cell.status !== 'accepted'
    const inputId = `pl-${year}-${field}`
    const key = `${year}.${field}`
    return (
      <div key={label} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 py-1.5 border-b border-border/40 last:border-0">
        <label htmlFor={inputId} className="text-xs font-medium text-brand-text w-36 shrink-0">
          {label}
        </label>
        <div className="flex items-center gap-1.5 ml-auto">
          {cell && <ProvenanceBadge cell={cell} edited={edited} currency={currency} />}
          <Input
            id={inputId}
            // CURRENCY-1: text + inputMode="decimal" rather than type="number" —
            // a number input cannot show thousands separators while typing, and
            // on iOS it silently rejects the grouped string.
            type="text"
            inputMode="decimal"
            value={plDraft[key] ?? numericInputValue(val)}
            placeholder="0.00"
            aria-label={`FY${year} ${label}`}
            onChange={e => {
              const shown = formatNumericInput(e.target.value)
              setPlDraft(prev => ({ ...prev, [key]: shown }))
              updatePL(year, field, parseNumericInput(shown))
            }}
            onBlur={() => setPlDraft(prev => {
              const { [key]: _drop, ...rest } = prev
              return rest
            })}
            className={`h-7 w-36 text-xs text-right tabular-nums bg-background border-2 ${
              draft ? 'border-amber-500/70' : 'border-input'
            } focus-visible:border-[var(--color-primary)]`}
          />
        </div>
      </div>
    )
  }

  // Computed cells are derived from the inputs, so they have no provenance of
  // their own — the machine never pre-fills a total.
  const renderPLComputed = (year: number, label: string, value: number | '') => (
    <div
      key={label}
      className="-mx-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-border/40 bg-muted/40 px-3 py-1.5 last:border-0"
    >
      <span className="w-36 shrink-0 text-xs text-muted-foreground">
        {label}
        <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground/70">
          calculated
        </span>
      </span>
      <span
        aria-label={`FY${year} ${label}`}
        className="ml-auto w-36 text-right text-xs font-semibold tabular-nums text-brand-text"
      >
        {value === '' ? '—' : fmtCurrency(value)}
      </span>
    </div>
  )

  // ── JSX ──
  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg font-semibold text-brand-text">EBITDA Normalization</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Quality of Earnings — Analyst Workspace</p>
        </div>
        {/* DEAL-PERSIST-1: the save state stays on screen until it changes —
            an indicator that fades back to blank after three seconds cannot be
            checked, and "did that save?" is the question this answers. */}
        <div className="text-xs tabular-nums min-w-36 text-right" aria-live="polite">
          {saveStatus === 'dirty' && (
            <span className="text-amber-600">Unsaved changes…</span>
          )}
          {saveStatus === 'saving' && <span className="text-muted-foreground">Saving…</span>}
          {saveStatus === 'saved' && (
            <span className="flex items-center justify-end gap-1 text-emerald-600">
              <CheckCircle2Icon className="h-3.5 w-3.5" /> All changes saved
            </span>
          )}
          {saveStatus === 'error' && (
            <button onClick={retrySave} className="text-destructive hover:underline">
              Save failed — retry
            </button>
          )}
        </div>
      </div>

      {/* Deal selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="deal-select" className="text-sm font-medium text-brand-text whitespace-nowrap">Deal</label>
        <select
          id="deal-select"
          value={dealId ?? ''}
          onChange={e => selectDeal(e.target.value || null)}
          className="h-8 max-w-xs rounded-lg border border-input bg-transparent px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">— Select a deal —</option>
          {/* The deals list arrives asynchronously. Without an option for the
              current selection the browser falls back to the placeholder, which
              reads as "the app lost my deal" even though it never did. */}
          {dealId && !deals.some(d => d.deal_id === dealId) && (
            <option value={dealId}>{deal?.business_name ?? 'Loading deal…'}</option>
          )}
          {deals.map(d => (
            <option key={d.deal_id} value={d.deal_id}>{d.business_name}</option>
          ))}
        </select>
        {/* CURRENCY-1: the currency every figure on this deal is reported in.
            Defaulted from the browser's own locale, never from IP geolocation. */}
        <label
          htmlFor="deal-currency"
          className="text-sm font-medium text-brand-text whitespace-nowrap"
        >
          Currency
        </label>
        <select
          id="deal-currency"
          value={currency}
          disabled={!dealId || savingCurrency}
          onChange={e => changeCurrency(e.target.value)}
          title="All figures on this engagement are shown in this currency"
          className="h-8 rounded-lg border border-input bg-transparent px-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        >
          {(CURRENCY_OPTIONS.some(o => o.code === currency)
            ? CURRENCY_OPTIONS
            : [{ code: currency, label: currency }, ...CURRENCY_OPTIONS]
          ).map(o => (
            <option key={o.code} value={o.code}>{o.label}</option>
          ))}
        </select>
        {deal?.loi_price != null && (
          <span className="text-xs text-muted-foreground">
            LOI: {fmtCurrency(deal.loi_price)}
          </span>
        )}
      </div>

      {/* ── REPORTGATE-WIRE-1: statement sign-off — THE report gate ──
          The Report Generator's button was gated on "zero PENDING findings",
          which nothing in this workspace could ever satisfy, so it was
          permanently disabled. The gate is now this explicit CPA sign-off.
          Placed directly under the deal selector rather than in the 280px rail:
          it is the action that unblocks the deliverable, and an analyst who
          cannot perform it still has to see that it is what they are waiting
          for. Editing the figures afterwards reopens it (server-side), so the
          badge is read from the session rather than held in local state. */}
      {dealId && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3"
          data-testid="normalization-approval"
        >
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-brand-text">Normalization Sign-Off</p>
              {normalization?.status === 'approved' ? (
                <Badge
                  className="bg-emerald-100 text-emerald-800 text-[10px] hover:bg-emerald-100"
                  data-testid="normalization-status-badge"
                >
                  Approved
                </Badge>
              ) : (
                <Badge
                  className="bg-amber-100 text-amber-800 text-[10px] hover:bg-amber-100"
                  data-testid="normalization-status-badge"
                >
                  Pending CPA Approval
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {normalization?.status === 'approved' ? (
                <>
                  Signed off by {normalization.approved_by ?? 'a CPA'}
                  {normalization.approved_at
                    ? ` on ${new Date(normalization.approved_at).toLocaleString()}`
                    : ''}
                  . Editing the figures below reopens the sign-off.
                </>
              ) : canApprove ? (
                'Approve the normalized statement to enable report generation.'
              ) : (
                'A CPA must approve this statement before a report can be generated.'
              )}
            </p>
          </div>
          {canApprove && normalization?.status !== 'approved' && (
            <Button
              size="sm"
              onClick={approveNormalization}
              disabled={approving || loadingNorm}
              data-testid="approve-normalization"
            >
              {approving ? 'Approving…' : 'Approve & Complete Normalization'}
            </Button>
          )}
        </div>
      )}

      {/* Financial Statement approval status */}
      {dealId && qoeFs && (
        <QoEFinancialStatementCard
          fs={qoeFs}
          onStatusChange={() => {
            apiClient
              .get<QoEFinancialStatement[]>(`/v1/qoe/deals/${dealId}/financial-statements`, { params: { status: 'approved', limit: 1 } })
              .then(r => setQoeFs(r.data[0] ?? null))
              .catch(() => setQoeFs(null))
          }}
        />
      )}

      {/* Extraction pre-fill (DOC-INTAKE-1 Job 2) */}
      {dealId && canApprove && (
        <ExtractionPanel
          dealId={dealId}
          visibleYears={years}
          provenance={provenance}
          lastRun={lastRun}
          currency={currency}
          // EXTRACT-REFRESH-1: the panel used to consider itself idle the
          // instant the POST resolved, even though the three GETs this bump
          // triggers (normalization, extraction, flexible-items) were still
          // in flight — so a CPA staring at a "Run extraction" button that
          // looked ready would re-click, and re-run a real extraction, while
          // the first run's reload was still outstanding. `refreshing` tells
          // the panel that reload isn't done yet, whatever triggered it.
          refreshing={loadingNorm || provenanceLoading || loadingFlexible}
          onExtracted={() => setExtractionNonce(n => n + 1)}
        />
      )}

      {/* 3-column workspace */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(260px,1fr)_minmax(420px,1.4fr)_280px] gap-4">

        {/* ── Left: P&L Input ── */}
        <div className="rounded-xl border border-border bg-card p-3">
          <h2 className="text-sm font-semibold text-brand-text mb-2">P&amp;L Input</h2>
          {loadingNorm ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}
            </div>
          ) : (
            <>
              {/* WORKSPACE-INPUT-1(a): say plainly what to type and what the
                  page works out for you, before the first field. */}
              <div className="mb-2 rounded-lg border border-border bg-muted/40 p-2 text-[11px] leading-relaxed">
                <p className="text-muted-foreground">
                  <span className="font-semibold text-brand-text">Enter these figures</span> from
                  the financial statements: Revenue, COGS, Operating Expenses, Depreciation,
                  Amortization, Interest Expense and Tax Expense.
                </p>
                <p className="mt-1 text-muted-foreground">
                  <span className="font-semibold text-brand-text">Calculated for you:</span> Gross
                  Profit, Net Income and EBITDA. They update as you type and cannot be edited.
                </p>
              </div>

              {/* WORKSPACE-INPUT-1(c): active fiscal year, unmistakably.
                  EXTRACT-PERIOD-1: the year list is no longer three by
                  construction, so the strip scrolls rather than crushing six
                  tabs into 375px. `min-w-` keeps "FY2024" readable at the
                  narrowest viewport we support. */}
              <div
                role="tablist"
                aria-label="Fiscal year"
                className="mb-2 flex w-full gap-1 overflow-x-auto rounded-lg bg-muted p-1"
              >
                {years.map(y => {
                  const isActive = y === activeYear
                  return (
                    <button
                      key={y}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveYear(y)}
                      className={`min-w-[4.25rem] flex-1 shrink-0 rounded-md px-2 py-1.5 text-xs transition-colors ${
                        isActive
                          ? 'bg-[var(--color-primary)] font-bold text-white shadow-sm ring-1 ring-[var(--color-primary)]'
                          : 'font-medium text-muted-foreground hover:bg-background/70 hover:text-brand-text'
                      }`}
                    >
                      FY{y}
                    </button>
                  )
                })}
              </div>

              {(() => {
                const y = activeYear
                // FIELDS-2: the computed rows read `activeDerived` (which this grid's
                // year drives) rather than re-deriving from `pl[y]`, so a custom line
                // item lands in Gross Profit / Net Income / EBITDA here exactly as it
                // does in the Summary panel. The INPUT rows above stay reported-only —
                // a flexible item is an additive layer, never an edit to what the
                // statements said.
                const gp = activeDerived.gross_profit
                const ni = activeDerived.net_income
                const eb = activeDerived.ebitda
                return (
                  <div>
                    {renderPLInput(y, 'revenue', `Revenue (${symbol})`)}
                    {renderPLInput(y, 'cogs', `COGS (${symbol})`)}
                    {renderPLComputed(y, 'Gross Profit', gp)}
                    {dealId && (
                      <AddLineItemInline
                        dealId={dealId}
                        year={y}
                        defaultTreatment="revenue"
                        onCreated={flexChanged}
                      />
                    )}
                    {renderPLInput(y, 'operating_expenses', `Operating Expenses (${symbol})`)}
                    {renderPLInput(y, 'depreciation', `Depreciation (${symbol})`)}
                    {renderPLInput(y, 'amortization', `Amortization (${symbol})`)}
                    {renderPLInput(y, 'interest', `Interest Expense (${symbol})`)}
                    {renderPLInput(y, 'tax', `Tax Expense (${symbol})`)}
                    {dealId && (
                      <AddLineItemInline
                        dealId={dealId}
                        year={y}
                        defaultTreatment="opex"
                        onCreated={flexChanged}
                      />
                    )}
                    {renderPLComputed(y, 'Net Income', ni)}
                    {renderPLComputed(y, 'EBITDA', eb)}
                  </div>
                )
              })()}
            </>
          )}
        </div>

        {/* ── Center: Adjustments + Chart ── */}
        <div className="flex flex-col gap-4">
          {/* Adjustments table */}
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-brand-text">EBITDA Adjustments</h2>
              <Button size="sm" variant="outline" onClick={addAdj} className="h-7 gap-1.5 text-xs">
                <PlusIcon className="h-3.5 w-3.5" /> Add Row
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map(hg => (
                    <TableRow key={hg.id}>
                      {hg.headers.map(h => (
                        <TableHead key={h.id} className="text-xs h-8 px-2 whitespace-nowrap">
                          {flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-4">
                        No adjustments — click &quot;Add Row&quot; to begin
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id} className="px-2 py-1">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {/* FIELDS-2: an add-back that is not one of the standard categories.
                It lands in the Custom Line Items panel, not this table, because a
                flexible item has provenance and a treatment that an adjustment row
                does not — but it raises Adj. EBITDA identically. */}
            {dealId && (
              <AddLineItemInline
                dealId={dealId}
                year={activeYear}
                defaultTreatment="add_back"
                onCreated={flexChanged}
              />
            )}
            <div className="mt-2 text-right text-xs font-semibold text-brand-primary">
              Total Adjustments: {fmtCurrency(totalAdj)}
            </div>
            {pendingCount > 0 && (
              <p className="mt-1 text-right text-xs text-muted-foreground">
                {pendingCount} still needed — excluded from the total, Adj. EBITDA and SDE
                until supported.
              </p>
            )}
          </div>

          {/* P&L Bridge chart */}
          <div className="rounded-xl border border-border bg-card p-3">
            <h2 className="text-sm font-semibold text-brand-text mb-2">P&amp;L Bridge — FY{activeYear}</h2>
            {bridgeImplausible && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-2">
                A figure on this year is at trillion scale — worth checking for a stray
                digit. The value is charted and saved as entered.
              </p>
            )}
            <motion.div
              key={waterfallData.map(d => d.value).join('-')}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={waterfallData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tickFormatter={fmtAxis}
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={bridgeAxisWidth}
                    {...(bridgeScale
                      ? { domain: bridgeScale.domain, ticks: bridgeScale.ticks }
                      : {})}
                  />
                  <Tooltip
                    formatter={(val) => [typeof val === 'number' ? fmtCurrency(val) : '—', '']}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Bar dataKey="value" isAnimationActive radius={[4, 4, 0, 0]} maxBarSize={60}>
                    {/* WORKSPACE-INPUT-1(d): read the figure off the bar itself
                        rather than eyeballing it against the axis.
                        WS-VIEW-3: placement is computed (chartScale.placeBarLabel)
                        so a short bar's value cannot land on its category tick. */}
                    <LabelList dataKey="value" content={bridgeLabel} />
                    {waterfallData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={BAR_COLOR}
                        fillOpacity={idx === waterfallData.length - 1 ? 1 : 0.55}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        </div>

        {/* ── Right: Summary Panel ── */}
        <div className="rounded-xl border border-border bg-card p-3 space-y-4">
          <h2 className="text-sm font-semibold text-brand-text">Summary — FY{activeYear}</h2>

          <div>
            <p className="text-xs text-muted-foreground">Adj. EBITDA</p>
            <p className="text-2xl font-bold text-brand-primary tabular-nums">
              {adjEbitda != null ? fmtCurrency(adjEbitda) : '—'}
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground">
              SDE <span className="text-[10px]">(+ Owner Comp adj.)</span>
            </p>
            <p className="text-xl font-semibold tabular-nums">
              {sde != null ? fmtCurrency(sde) : '—'}
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground">
              Multiple{' '}
              {deal?.loi_price != null
                ? <span className="text-[10px]">(LOI ÷ SDE, truncated)</span>
                : <span className="text-[10px]">(set LOI on deal)</span>
              }
            </p>
            <p className="text-xl font-semibold tabular-nums">
              {sdeMultiple != null ? `${sdeMultiple}x` : '—'}
            </p>
          </div>

          <hr className="border-border" />

          {/* AI Narrative — ENG-AI-UI-RENDER-1. The panel and its three
              endpoints moved to AiNarrativePanel + @/api/narrative; the
              draft now appears in place the moment it is saved, and the
              statement gate (`normalization.status`) is no longer in reach
              of a narrative response. */}
          <AiNarrativePanel
            dealId={dealId}
            narrative={narrative}
            role={user?.role}
            onFlush={flushBeforeNarrative}
            onChange={patchNarrative}
          />
        </div>
      </div>

      {/* ── FIELDS-1 P1a: balance sheet ──
          Full width, below the grid: five line items x three fiscal years does
          not fit the 260px P&L rail, and the brief asks for one COLUMN per
          period. Self-contained (own load + explicit Save) rather than folded
          into buildPayload, because the page's 2s autosave is keyed to the P&L
          refs and the save route is preserve-on-omit — so the two panels write
          disjoint keys and cannot clobber each other. */}
      {/* EXTRACT-PERIOD-2: keyed on the extraction nonce because this panel
          derives its own period columns from the deal's financial_years and
          only reloads when `dealId` changes. Without the key an extraction that
          widened the P&L to four periods left the balance sheet showing three
          — two panels on one screen disagreeing about how many years the deal
          has. */}
      {dealId && <BalanceSheetInputs key={extractionNonce} dealId={dealId} />}

      {/* ── FIELDS-1 P1b: equity bridge intake ──
          Directly under the balance sheet, because the two are read together:
          the bridge falls back to the balance sheet for deferred revenue and
          customer deposits, and derives the working-capital true-up from the peg the balance
          sheet produces. Same self-contained pattern — disjoint keys, own Save.
          The endpoint's pending note tells the CPA to enter the enterprise value
          "in the Normalization workspace", so it has to actually be here. */}
      {dealId && <EquityBridgeInputs dealId={dealId} />}

      {/* ── FIELDS-2: custom line items ──
          Below the bridge intake because a debt-like item feeds it, and full
          width because the review queue needs room for provenance ("extracted
          from X, cell Y") — the whole reason an unclassified extracted line is
          actionable rather than just a mystery number. This is the ONE place
          every custom item is visible regardless of year or treatment; the
          inline "+ Add line item" affordances above only create them. */}
      {dealId && (
        <FlexibleLineItemsPanel
          dealId={dealId}
          items={flexibleItems}
          routing={flexibleRouting}
          treatments={flexibleMeanings}
          loading={loadingFlexible}
          years={years}
          onChanged={flexChanged}
          fmtCurrency={fmtCurrency}
        />
      )}

      {/* ── WS-VIEW-2: multi-year rollup ──
          The panels above are single-year by design (one FY tab at a time), so
          the trend — the thing a buyer actually underwrites — was invisible in
          the workspace and only appeared once the report was generated. Full
          width, below the grid, because three years x four metrics does not fit
          a 280px rail. Figures come from `buildMultiYear`, i.e. the same
          `derivePeriod` the Summary panel uses, so the two cannot disagree.
          Multi-YEAR only: entity consolidation is CONSOL-FIX-1, not this. */}
      <MultiYearTrendCard
        years={years}
        pl={pl}
        adjustments={adjustments}
        flexibleItems={flexibleItems}
        fmtCurrency={fmtCurrency}
        fmtAxis={fmtAxis}
        activeYear={activeYear}
        onSelectYear={setActiveYear}
      />
    </div>
  )
}
